import { useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
  Settings,
  Info,
  Eye,
  EyeOff,
  Save,
  Check,
  X,
  RotateCcw,
  Sliders,
  Upload,
  Image as ImageIcon,
  Trash2,
  Loader2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { configAPI, resolveAssetUrl, type ConfigEntry } from '../services/api';

/**
 * Configurações — layout pai. Cada sub-aba é uma rota nested em App.tsx
 * (/configuracoes/{usuarios,aplicacao,token,app-central,ia,parametrizacao}).
 *
 * O Sidebar é a nav (não duplicamos com tabs horizontais aqui). Por ora cada
 * aba mostra as env vars relevantes; edição via .env por enquanto.
 * Endpoint de salvar configs no Mongo vem em fase futura conforme cada
 * cliente precise.
 */
export default function ConfiguracoesLayout() {
  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto">
      <header className="mb-8 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-primary-soft/40 text-primary flex items-center justify-center">
          <Settings size={24} />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-fg">Configurações</h1>
          <p className="text-sm text-fg-2">Aplicação, integrações e parâmetros</p>
        </div>
      </header>

      <Outlet />
    </div>
  );
}

// ============================================================================
// Helpers compartilhados pelas sub-abas
// ============================================================================

function PageCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-6">{children}</div>
  );
}

function useIsAdmin(): boolean {
  const { user } = useAuth();
  return user?.role === 'admin';
}

// ============================================================================
// Editable config form — usado por tabs que persistem em /api/config
// ============================================================================

interface EditableField {
  key: string;
  label: string;
  description: string;
  placeholder?: string;
  type?: 'text' | 'password' | 'url';
}

function EditableConfigForm({
  fields,
  onSaved,
}: {
  fields: EditableField[];
  onSaved?: () => void;
}) {
  const isAdmin = useIsAdmin();
  const [entries, setEntries] = useState<Map<string, ConfigEntry>>(new Map());
  const [values, setValues] = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await configAPI.list();
      const map = new Map(r.data.map((e) => [e.key, e]));
      setEntries(map);
      // Pre-popula values: usa o que veio do servidor (já mascarado quando encrypted)
      const initialValues: Record<string, string> = {};
      for (const f of fields) {
        const entry = map.get(f.key);
        initialValues[f.key] = entry?.encrypted ? '' : entry?.value ?? '';
      }
      setValues(initialValues);
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message ?? e.message ?? 'Erro ao carregar configurações.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleSave(field: EditableField): Promise<void> {
    const value = values[field.key]?.trim();
    if (!value) {
      setError(`${field.label}: valor não pode ser vazio.`);
      return;
    }
    setSaving(field.key);
    setError(null);
    try {
      await configAPI.set(field.key, value);
      setSavedKey(field.key);
      setTimeout(() => setSavedKey(null), 2000);
      await load();
      onSaved?.();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message ?? e.message ?? 'Erro ao salvar.');
    } finally {
      setSaving(null);
    }
  }

  async function handleReset(field: EditableField): Promise<void> {
    if (!confirm(`Remover override do ${field.label}? Vai voltar a usar o valor do .env.`)) {
      return;
    }
    setSaving(field.key);
    setError(null);
    try {
      await configAPI.remove(field.key);
      await load();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e.response?.data?.message ?? e.message ?? 'Erro ao remover.');
    } finally {
      setSaving(null);
    }
  }

  if (!isAdmin) {
    return (
      <div className="text-sm text-fg-2 italic">
        Apenas admin pode editar estas configurações.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {fields.map((f) => (
          <div key={f.key} className="h-20 bg-canvas border border-line rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-danger-soft/20 border border-danger-line/50 text-sm text-danger">
          <X size={16} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {fields.map((field) => {
        const entry = entries.get(field.key);
        const isSecret = field.type === 'password' || entry?.encrypted;
        const showSecret = showSecrets[field.key];
        const inMongo = !!entry;
        const isSaving = saving === field.key;
        const justSaved = savedKey === field.key;

        return (
          <div
            key={field.key}
            className="rounded-lg bg-canvas border border-line px-4 py-3"
          >
            <div className="flex items-baseline justify-between mb-1">
              <div className="flex items-center gap-2">
                <code className="text-sm font-mono text-primary">{field.key}</code>
                {inMongo ? (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-primary-soft/40 text-primary border border-primary-line/50">
                    Mongo
                  </span>
                ) : (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-hover text-fg-2 border border-active">
                    .env
                  </span>
                )}
              </div>
              {justSaved && (
                <span className="text-xs text-success flex items-center gap-1">
                  <Check size={12} />
                  Salvo
                </span>
              )}
            </div>
            <p className="text-xs text-muted mb-3">{field.description}</p>

            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type={isSecret && !showSecret ? 'password' : 'text'}
                  value={values[field.key] ?? ''}
                  onChange={(e) =>
                    setValues((v) => ({ ...v, [field.key]: e.target.value }))
                  }
                  placeholder={
                    field.placeholder ??
                    (isSecret && inMongo ? 'Digite pra substituir o valor atual' : '')
                  }
                  disabled={isSaving}
                  className="w-full px-3 py-2 bg-surface border border-line rounded-lg text-fg text-sm placeholder-slate-600 focus:border-primary focus:outline-none disabled:opacity-50"
                />
                {isSecret && (
                  <button
                    type="button"
                    onClick={() => setShowSecrets((s) => ({ ...s, [field.key]: !s[field.key] }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted hover:text-fg-2"
                    aria-label={showSecret ? 'Ocultar' : 'Mostrar'}
                  >
                    {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => void handleSave(field)}
                disabled={isSaving || !values[field.key]?.trim()}
                className="flex items-center gap-1.5 px-3 py-2 bg-primary-solid hover:bg-primary-solid-hover disabled:bg-active disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition"
              >
                <Save size={14} />
                {isSaving ? 'Salvando…' : 'Salvar'}
              </button>

              {inMongo && (
                <button
                  type="button"
                  onClick={() => void handleReset(field)}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 px-3 py-2 bg-hover hover:bg-active disabled:opacity-50 text-fg rounded-lg text-sm transition"
                  title="Remover override e voltar ao valor do .env"
                >
                  <RotateCcw size={14} />
                </button>
              )}
            </div>

            {isSecret && inMongo && (
              <p className="text-xs text-faint mt-2">
                Valor atual: <code className="font-mono text-muted">{entry?.value}</code>{' '}
                (mascarado)
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Sub-abas (cada uma é uma rota — montadas em App.tsx)
// ============================================================================

export function Aplicacao() {
  const { refresh } = useBranding();
  return (
    <PageCard>
      <h2 className="text-lg font-semibold text-fg mb-2">Aplicação</h2>
      <p className="text-sm text-fg-2 mb-4">
        Identidade do deploy — nome e logo exibidos no header do painel, página
        pública de agendamento e relatórios.
      </p>
      <EditableConfigForm
        fields={[
          {
            key: 'CLIENT_NAME',
            label: 'Nome do cliente',
            description: 'Aparece no header, login e página pública.',
            placeholder: 'Ex: Provedor X',
          },
        ]}
        onSaved={() => void refresh()}
      />
      <LogoUploader />

      <div className="border-t border-line mt-8 pt-6">
        <h2 className="text-lg font-semibold text-fg mb-2">Tela de login</h2>
        <p className="text-sm text-fg-2 mb-4">
          Personalize o painel de anúncio (lado direito) e o aviso abaixo do
          formulário. Campos vazios não aparecem — sem título/texto, o painel
          mostra o nome do cliente sobre o gradiente padrão.
        </p>
        <EditableConfigForm
          fields={[
            {
              key: 'LOGIN_BANNER_BADGE',
              label: 'Selo do anúncio',
              description: 'Pílula no topo do painel. Ex: "Webinar | 21/07 às 15h".',
              placeholder: 'Ex: Novidade',
            },
            {
              key: 'LOGIN_BANNER_TITLE',
              label: 'Título do anúncio',
              description: 'Texto grande em destaque no painel direito.',
              placeholder: 'Ex: Conheça o novo módulo de agendamento',
            },
            {
              key: 'LOGIN_BANNER_BODY',
              label: 'Texto do anúncio',
              description: 'Parágrafo abaixo do título (quebras de linha são respeitadas).',
              placeholder: 'Descreva o aviso ou anúncio…',
            },
            {
              key: 'LOGIN_BANNER_CTA_TEXT',
              label: 'Texto do botão',
              description: 'Botão de ação do anúncio. Vazio = sem botão.',
              placeholder: 'Ex: Saiba mais',
            },
            {
              key: 'LOGIN_BANNER_CTA_URL',
              label: 'Link do botão',
              description: 'URL aberta ao clicar no botão (nova aba).',
              placeholder: 'https://…',
              type: 'url',
            },
            {
              key: 'LOGIN_NOTICE',
              label: 'Aviso no formulário',
              description:
                'Caixa informativa abaixo do botão Entrar (ex: instruções de acesso).',
              placeholder: 'Ex: O acesso ao painel foi unificado…',
            },
          ]}
          onSaved={() => void refresh()}
        />
        <LoginBannerUploader />
      </div>
    </PageCard>
  );
}

// ============================================================
// LoginBannerUploader — carrossel de imagens do login (Fase 28/30)
// ============================================================

function LoginBannerUploader() {
  const isAdmin = useIsAdmin();
  const { login, refresh } = useBranding();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  if (!isAdmin) return null;

  const images = (login?.images ?? []).filter(Boolean);

  async function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    if (file.size > 5 * 1024 * 1024) {
      setError('Arquivo grande demais (máx 5 MB).');
      return;
    }
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type)) {
      setError('Use PNG, JPG, WEBP ou SVG.');
      return;
    }

    setUploading(true);
    try {
      await configAPI.uploadLoginBanner(file);
      await refresh();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (err) {
      const e2 = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e2.response?.data?.message ?? e2.message ?? 'Erro ao enviar.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleRemove(url: string) {
    if (!confirm('Remover esta imagem do carrossel do login?')) return;
    setUploading(true);
    setError(null);
    try {
      await configAPI.removeLoginBanner(url);
      await refresh();
    } catch (err) {
      const e2 = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e2.response?.data?.message ?? e2.message ?? 'Erro ao remover.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="mt-5 rounded-lg border border-line bg-canvas p-4">
      <div className="flex items-baseline justify-between mb-1">
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono text-primary">LOGIN_BANNER_IMAGES</code>
          {images.length > 0 ? (
            <span className="text-xs px-1.5 py-0.5 rounded bg-primary-soft/40 text-primary border border-primary-line/50">
              {images.length} imagem(ns)
            </span>
          ) : (
            <span className="text-xs px-1.5 py-0.5 rounded bg-hover text-fg-2 border border-active">
              não definido
            </span>
          )}
        </div>
        {savedAt && (
          <span className="text-xs text-success flex items-center gap-1">
            <Check size={12} /> Salvo
          </span>
        )}
      </div>
      <p className="text-xs text-muted mb-3">
        Imagens do lado direito do login — aparecem nítidas, sem texto por cima.
        Com mais de uma, viram um carrossel automático (até 6). PNG/JPG/WEBP até
        5 MB; recomendado retrato/paisagem grande ~1600×1200 px.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        {images.map((url) => {
          const src = resolveAssetUrl(url);
          return (
            <div
              key={url}
              className="relative w-36 h-20 rounded-xl bg-surface border border-line overflow-hidden flex-shrink-0 group"
            >
              {src && (
                <img src={src} alt="Imagem do login" className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={() => void handleRemove(url)}
                disabled={uploading}
                className="absolute top-1 right-1 p-1.5 rounded-lg bg-black/60 text-fg-2 hover:bg-danger-soft/80 hover:text-danger opacity-0 group-hover:opacity-100 transition"
                aria-label="Remover imagem"
              >
                <Trash2 size={13} />
              </button>
            </div>
          );
        })}

        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          onChange={handlePick}
          disabled={uploading}
          className="hidden"
        />
        {images.length < 6 && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-36 h-20 rounded-xl border-2 border-dashed border-active hover:border-primary text-muted hover:text-primary flex flex-col items-center justify-center gap-1 text-xs transition disabled:opacity-50"
          >
            {uploading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <>
                <Upload size={18} />
                Adicionar imagem
              </>
            )}
          </button>
        )}
        {images.length === 0 && !uploading && (
          <ImageIcon size={28} className="text-muted" />
        )}
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-danger-soft/20 border border-danger-line/50 text-sm text-danger">
          <X size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// ============================================================
// LogoUploader — upload + preview + remove
// ============================================================

function LogoUploader() {
  const isAdmin = useIsAdmin();
  const { clientName, logoSrc, refresh } = useBranding();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  if (!isAdmin) return null;

  async function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);

    if (file.size > 2 * 1024 * 1024) {
      setError('Arquivo grande demais (máx 2 MB).');
      return;
    }
    if (!/^image\/(png|jpe?g|webp|svg\+xml)$/.test(file.type)) {
      setError('Use PNG, JPG, WEBP ou SVG.');
      return;
    }

    setUploading(true);
    try {
      await configAPI.uploadLogo(file);
      await refresh();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (err) {
      const e2 = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e2.response?.data?.message ?? e2.message ?? 'Erro ao enviar.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleRemove() {
    if (!confirm('Remover o logo atual?')) return;
    setUploading(true);
    setError(null);
    try {
      await configAPI.removeLogo();
      await refresh();
    } catch (err) {
      const e2 = err as { response?: { data?: { message?: string } }; message?: string };
      setError(e2.response?.data?.message ?? e2.message ?? 'Erro ao remover.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="mt-5 rounded-lg border border-line bg-canvas p-4">
      <div className="flex items-baseline justify-between mb-1">
        <div className="flex items-center gap-2">
          <code className="text-sm font-mono text-primary">CLIENT_LOGO</code>
          {logoSrc ? (
            <span className="text-xs px-1.5 py-0.5 rounded bg-primary-soft/40 text-primary border border-primary-line/50">
              Mongo
            </span>
          ) : (
            <span className="text-xs px-1.5 py-0.5 rounded bg-hover text-fg-2 border border-active">
              não definido
            </span>
          )}
        </div>
        {savedAt && (
          <span className="text-xs text-success flex items-center gap-1">
            <Check size={12} /> Salvo
          </span>
        )}
      </div>
      <p className="text-xs text-muted mb-3">
        Logo exibido no header do painel e na página pública /agendar. PNG/JPG/WEBP/SVG
        até 2 MB. Recomendado: PNG transparente ~200×200 px.
      </p>

      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-xl bg-surface border border-line flex items-center justify-center overflow-hidden flex-shrink-0">
          {logoSrc ? (
            <img
              src={logoSrc}
              alt={clientName}
              className="w-full h-full object-contain p-1"
            />
          ) : (
            <ImageIcon size={28} className="text-faint" />
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={handlePick}
            disabled={uploading}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary-solid hover:bg-primary-solid-hover disabled:opacity-50 text-white rounded-lg text-sm font-medium transition"
          >
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? 'Enviando…' : logoSrc ? 'Trocar logo' : 'Enviar logo'}
          </button>
          {logoSrc && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-hover hover:bg-danger-soft/60 text-fg hover:text-danger rounded-lg text-sm transition"
            >
              <Trash2 size={14} />
              Remover
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-lg bg-danger-soft/20 border border-danger-line/50 text-sm text-danger">
          <X size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

export function TokenERP() {
  return (
    <PageCard>
      <h2 className="text-lg font-semibold text-fg mb-2">Token ERP (SGP)</h2>
      <p className="text-sm text-fg-2 mb-4">
        Credenciais pra integração com o SGP (sistema de gestão do ISP cliente). Usado
        pelos módulos central, agendamento e prospect pra consultar contratos, criar OS,
        autenticar clientes finais etc.
      </p>
      <div className="flex items-start gap-2 mb-5 rounded-lg border border-primary-line/50 bg-primary-soft/20 px-4 py-3 text-sm text-primary">
        <Info size={16} className="mt-0.5 flex-shrink-0" />
        <div>
          <strong>Editável em runtime.</strong> Valores salvos aqui sobrescrevem o{' '}
          <code className="bg-primary-soft/50 px-1 rounded">.env</code> e entram em vigor
          em até 30 segundos (sem restart). Clique no <RotateCcw size={12} className="inline" /> pra
          reverter pro <code className="bg-primary-soft/50 px-1 rounded">.env</code>.
        </div>
      </div>
      <EditableConfigForm
        fields={[
          {
            key: 'ERP_URL',
            label: 'URL do ERP',
            description: 'URL base do SGP do cliente (ex: https://provedor.sgp.tsmx.com.br).',
            type: 'url',
            placeholder: 'https://exemplo.sgp.tsmx.com.br',
          },
          {
            key: 'ERP_TOKEN',
            label: 'Token ERP',
            description: 'Token de autenticação fornecido pelo SGP.',
            type: 'password',
          },
          {
            key: 'ERP_APP',
            label: 'App ID',
            description: 'Identificador da aplicação no SGP (ex: agendaatlanti).',
          },
        ]}
      />
    </PageCard>
  );
}

export function IA() {
  return (
    <PageCard>
      <h2 className="text-lg font-semibold text-fg mb-2">IA Token</h2>
      <p className="text-sm text-fg-2 mb-4">
        Configurações pra integrações com modelos de IA (OpenAI, Anthropic, etc.) usadas
        pelos relatórios e análises automáticas.
      </p>
      <EditableConfigForm
        fields={[
          {
            key: 'IA_URL',
            label: 'URL da API',
            description: 'Endpoint do provedor (ex: https://api.openai.com/v1).',
            type: 'url',
            placeholder: 'https://api.openai.com/v1',
          },
          {
            key: 'IA_API_KEY',
            label: 'API Key',
            description: 'Chave de autenticação. Mantida criptografada e mascarada na UI.',
            type: 'password',
          },
          {
            key: 'IA_MODEL',
            label: 'Modelo',
            description: 'Identificador do modelo (ex: gpt-4o, claude-sonnet-4-5).',
            placeholder: 'gpt-4o',
          },
        ]}
      />
    </PageCard>
  );
}

export function Parametrizacao() {
  return (
    <PageCard>
      <div className="text-center py-10">
        <div className="w-14 h-14 rounded-2xl bg-canvas border border-line mx-auto flex items-center justify-center mb-4">
          <Sliders size={28} className="text-muted" />
        </div>
        <h2 className="text-lg font-semibold text-fg mb-2">Parametrização</h2>
        <p className="text-sm text-fg-2 max-w-md mx-auto">
          Em breve. Esta tela vai permitir ajustar parâmetros do SAAS — defaults de
          módulos, regras de negócio, automações.
        </p>
      </div>
    </PageCard>
  );
}
